import { Info } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAntiguedadDeuda } from '@/hooks/useReporteFinanciero'
import { mensajeErrorDeCarga } from '@/lib/permisos-guard'
import { cn, formatCurrency } from '@/lib/utils'
import type { TramoAntiguedad } from '@/services/reporteFinancieroService'

/** Rótulo y color de barra de cada tramo que devuelve reporte_antiguedad_deuda. */
const TRAMOS: Record<TramoAntiguedad, { label: string; barra: string }> = {
  '0-29': { label: 'Menos de 30 días', barra: 'bg-green-500' },
  '30-60': { label: '30 a 60 días', barra: 'bg-amber-500' },
  '61-90': { label: '61 a 90 días', barra: 'bg-orange-500' },
  '90+': { label: 'Más de 90 días', barra: 'bg-red-600' },
  sin_imputar: { label: 'Pagos sin imputar', barra: 'bg-muted-foreground/40' },
}

const AYUDA =
  'Deuda impaga de los clientes activos al día de hoy, por los días desde la fecha de cada deuda. No depende del período elegido. «Pagos sin imputar» son pagos que no se aplicaron a ninguna deuda y se restan del total.'

/**
 * Antigüedad de la deuda de los clientes activos al día de hoy, con los tramos de la RPC.
 * Cada deuda cuenta por su edad. Los pagos sin imputar van con signo negativo, así el total da la deuda de activos.
 */
export function AntiguedadDeuda() {
  const { data: tramos, isLoading, isError, error } = useAntiguedadDeuda()
  const total = tramos?.reduce((acc, t) => acc + t.monto, 0) ?? 0
  const mayor = Math.max(0, ...(tramos ?? []).map((t) => t.monto))

  return (
    <Card role="region" aria-labelledby="antiguedad-deuda-titulo">
      <CardHeader className="pb-2">
        <CardTitle
          id="antiguedad-deuda-titulo"
          className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground"
        >
          Antigüedad de la deuda
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" aria-label="Qué mide Antigüedad de la deuda" className="text-muted-foreground/70">
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{AYUDA}</TooltipContent>
          </Tooltip>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {isError ? (
          <p className="text-sm text-destructive">{mensajeErrorDeCarga(error)}</p>
        ) : isLoading || !tramos ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            {tramos.map((t) => {
              const { label, barra } = TRAMOS[t.tramo]
              const ancho = mayor > 0 ? (Math.max(t.monto, 0) / mayor) * 100 : 0
              return (
                <div key={t.tramo} data-testid="tramo-antiguedad" className="flex items-center gap-2">
                  <div className="w-36 shrink-0">
                    <p className="text-sm">{label}</p>
                    <p className="text-xs text-muted-foreground">
                      {t.deudas} {t.tramo === 'sin_imputar' ? 'pagos' : 'deudas'} · {t.clientes}{' '}
                      {t.clientes === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                    <div className={cn('h-full', barra)} style={{ width: `${ancho}%` }} />
                  </div>
                  <span className="w-28 whitespace-nowrap text-right text-sm font-medium">{formatCurrency(t.monto)}</span>
                </div>
              )
            })}
            <div className="flex items-center justify-between border-t pt-2 text-sm font-semibold">
              <span>Por cobrar hoy</span>
              <span>{formatCurrency(total)}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
